import { useState } from "react";
import { useSelector } from "react-redux";

import DetectionDetailPopup from "./DetectionDetailPopup";

const normalizePath = (path) =>
  path ? String(path).trim().toLowerCase().replace(/[\\/]+/g, "\\") : "";

const baseName = (path) => {
  const normalized = normalizePath(path);
  if (!normalized) return "";
  const parts = normalized.split("\\");
  return parts[parts.length - 1];
};

// Finds the detection_details entry (file or process) behind a file path,
// process name/pid or hash, and shows it in a popup with the same content
// as the thunter-additional-data section.
const useDetectionDetailPopup = () => {
  const detectionDetails = useSelector(
    (state) => state.integrations.agentAnalysisData?.detection_details,
  );

  const [item, setItem] = useState(null);

  const files = detectionDetails?.files || [];
  const processes = detectionDetails?.processes || [];

  const findFileDetail = (filePath) => {
    const target = normalizePath(filePath);
    if (!target) return null;
    return files.find((file) => normalizePath(file.file) === target) || null;
  };

  const findProcessDetail = ({ process, pid } = {}) => {
    const targetName = process ? String(process).trim().toLowerCase() : "";
    if (targetName) {
      const match = processes.find((p) => {
        const name = String(p.process || "").trim().toLowerCase();
        return name === targetName || baseName(p.process) === targetName;
      });
      if (match) return match;
    }
    if (pid !== undefined && pid !== null && pid !== "") {
      return processes.find((p) => String(p.pid) === String(pid)) || null;
    }
    return null;
  };

  const findDetailByHash = (hash) => {
    const target = hash ? String(hash).trim().toLowerCase() : "";
    if (!target) return null;
    const matchesHash = (entry) =>
      entry.hashes &&
      [entry.hashes.md5, entry.hashes.sha1, entry.hashes.sha256].some(
        (h) => h && String(h).toLowerCase() === target,
      );
    return files.find(matchesHash) || processes.find(matchesHash) || null;
  };

  // For sigma outputs: check every value of the output match against an index
  // of detected file paths/names and process names/pids. Comparisons ignore
  // case and normalize escape characters in paths (\\ vs \).
  const findDetailByOutputMatch = (output) => {
    if (!output || typeof output !== "object") return null;

    const entries = Object.entries(output).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    );
    const values = entries.map(([, value]) => String(value));
    const normalizedValues = values.map((value) => normalizePath(value));

    // 1. Full file path match
    for (const file of files) {
      const filePath = normalizePath(file.file);
      if (filePath && normalizedValues.some((value) => value === filePath)) return file;
    }

    // 2. Process name match (exact name or basename of a path value)
    for (const process of processes) {
      const name = String(process.process || "").trim().toLowerCase();
      if (
        name &&
        values.some(
          (value) => value.trim().toLowerCase() === name || baseName(value) === name,
        )
      ) {
        return process;
      }
    }

    // 3. PID match, only against pid-like fields (ProcessID, ParentProcessId, ...)
    const pidValues = entries
      .filter(([key]) => key.toLowerCase().includes("pid") || key.toLowerCase().includes("processid"))
      .map(([, value]) => String(value).trim());
    if (pidValues.length) {
      const pidMatch = processes.find(
        (process) =>
          process.pid !== undefined &&
          process.pid !== null &&
          pidValues.includes(String(process.pid).trim()),
      );
      if (pidMatch) return pidMatch;
    }

    // 4. File name (basename) match
    for (const file of files) {
      const fileName = baseName(file.file);
      if (
        fileName &&
        values.some(
          (value) => value.trim().toLowerCase() === fileName || baseName(value) === fileName,
        )
      ) {
        return file;
      }
    }

    // 5. Value contains a known full path (e.g. command lines)
    for (const file of files) {
      const filePath = normalizePath(file.file);
      if (filePath && normalizedValues.some((value) => value.includes(filePath))) return file;
    }

    return null;
  };

  const openDetail = (detail) => {
    if (detail) setItem(detail);
  };

  const popup = item ? (
    <DetectionDetailPopup item={item} onClose={() => setItem(null)} />
  ) : null;

  return {
    findFileDetail,
    findProcessDetail,
    findDetailByHash,
    findDetailByOutputMatch,
    openDetail,
    popup,
  };
};

export default useDetectionDetailPopup;
